<template>
  <div class="liquidity-area">
    <WindowModal :title="modalTitle" type="Global">
      <template #modal-content>
        <ImportPool @select="reloadList" />
      </template>
    </WindowModal>
    <WindowModal :title="modalRemoveTitle" type="Remove">
      <template #modal-content>
        <RemoveLiquidity
          ref="removeLiquidity"
          :pool="pickedPool"
          :transaction="transaction"
          @removeLiquidity="removeLiquidity"
          @initSettingsState="changeModalTitle('settings')"
          @initConfirmState="changeModalTitle('init')"
          @initLoading="changeModalTitle('loading')"
          @initSuccess="changeModalTitle('success')"
          @initError="changeModalTitle('error')"
          @closeRemoveModal="closeModal"
        />
      </template>
    </WindowModal>
    <div class="liquidity-box">
      <div class="image-area">
        <div class="picture">
          <picture>
            <source srcset="/img/new/floatie-token.webp" type="image/webp">
            <nuxt-img
              fit="inside"
              width="490"
              sizes="sm:250px md:300px lg:350px xl:400px xxl:440px"
              src="/img/new/floatie-token.png"
              alt="Provide $FLOATIE liquidity pairs"
              title="$FLOATIE tokens stats, liquidity, price"
            />
          </picture>
        </div>
      </div>
      <h1>
        {{ $t('pool.liquidity_header') }}
      </h1>
      <p class="desc">
        {{ $t('pool.liquidity_subheader') }}
      </p>
      <nuxt-link class="independent-link" :to="localePath('/docs')">
        {{ $t('pool.read_more') }}
      </nuxt-link>
      <div class="divider-horizontal"></div>
      <div class="buttons-area">
        <nuxt-link class="btn cyan" :to="localePath('/liquidity/add')">
          {{ $t('pool.add_liquidity') }}
        </nuxt-link>
        <button v-show="user === undefined || user === null" type="button" class="btn pink" @click="openModalWallets">
          {{ $t('common.connect_wallet') }}
        </button>
        <button v-show="user !== undefined && user !== null" type="button" class="btn btn-outline pink" @click="openImportModal">
          <span class="text">
            {{ $t('pools.import_pool') }}
          </span>
        </button>
      </div>
      <div class="active-pools">
        <div v-if="(pairsListLoading || pairsList.length > 0) && user !== undefined && user !== null" class="divider-horizontal"></div>
        <section v-if="!pairsListLoading && pairsList.length > 0" class="pools-list">
          <div class="pools-header">
            <div class="h4">
              {{ $t('pools.your_liquidity') }}
            </div>
            <div class="hint-icon wide-tooltip" :tooltip="$t('pools.your_liquidity_hint')">
              <Question />
            </div>
          </div>
          <div
            v-for="(pool, index) in pairsList"
            :key="index"
            class="pool"
          >
            <div class="tokens">
              <div class="logos">
                <nuxt-img
                  :src="pool.token0.address !== $config.ZERO ? $getImageByAddress(pool.token0.address) : $getImage(pool.token0.logoURI)"
                  :alt="pool.token0.name + ' ' + $config.appName"
                  :title="pool.token0.name"
                  placeholder
                  :lazy="true"
                  @error="$event.target.src = require('~/static/img/default_token.svg')"
                />
                <nuxt-img
                  :src="pool.token1.address !== $config.ZERO ? $getImageByAddress(pool.token1.address) : $getImage(pool.token1.logoURI)"
                  :alt="pool.token0.name + ' ' + $config.appName"
                  :title="pool.token0.name"
                  placeholder
                  :lazy="true"
                  @error="$event.target.src = require('~/static/img/default_token.svg')"
                />
              </div>
              <p class="desc">
                {{ pool.token0.symbol + ' / ' + pool.token1.symbol }}
              </p>
            </div>
            <hr>
            <div class="pool-hidden">
              <div class="pool-row">
                <p>
                  {{ $t('pools.pooled') + ' ' + pool.token0.symbol + ':' }}
                </p>
                <p class="value">
                  {{ pool.reserves !== undefined ? $formatCryptoAmount(pool.poolShare * pool.reserves.reserve0, 6) : '...' }}
                </p>
              </div>
              <div class="pool-row">
                <p>
                  {{ $t('pools.pooled') + ' ' + pool.token1.symbol + ':' }}
                </p>
                <p class="value">
                  {{ pool.reserves !== undefined ? $formatCryptoAmount(pool.poolShare * pool.reserves.reserve1, 6) : '...' }}
                </p>
              </div>
              <div class="pool-row">
                <p>
                  {{ $t('pools.your_pooled_tokens') + ':' }}
                </p>
                <p class="value">
                  {{ $formatCryptoAmount(pool.balance, 6) }}
                </p>
              </div>
              <div class="pool-row">
                <p>
                  {{ $t('pools.your_pooled_share') + ':' }}
                </p>
                <p class="value">
                  {{ $priceFormat(pool.poolShare * 100, 2) + '%' }}
                </p>
              </div>
              <hr>
              <div class="pool-row">
                <button class="btn cyan" type="button" @click.prevent="addLiquidityRedirect(pool)">
                  {{ $t('pools.add') }}
                </button>
                <button class="btn pink" type="button" @click.prevent="prepareLiquidityRemovalApprove(pool, index)">
                  {{ $t('pools.remove') }}
                </button>
              </div>
            </div>
          </div>
        </section>
        <section v-else-if="pairsListLoading && user !== undefined && user !== null" class="loading-area">
          <div class="icon-area">
            <Loading />
          </div>
          <div class="h4">
            {{ $t('pools.import_pool_loading') }}
          </div>
        </section>
      </div>
    </div>
    <div class="cyan-cloud">
      <CloudCyanLiquidityLeft />
    </div>
    <div class="cyan-cloud2">
      <CloudCyanLiquidityRight />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  hydrateWhenIdle
} from 'vue-lazy-hydration'

import RemoveLiquidity from '~/components/app/interface/RemoveLiquidity'

export default {
  name: 'LiquidityComponent',
  components: {
    CloudCyanLiquidityLeft: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanLiquidityLeft')),
    CloudCyanLiquidityRight: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanLiquidityRight')),
    ImportPool: hydrateWhenIdle(() => import('~/components/app/interface/ImportPool')),
    Question: hydrateWhenIdle(() => import('~/components/svg/new/Question')),
    Loading: hydrateWhenIdle(() => import('~/components/svg/new/Loading')),
    RemoveLiquidity,
    WindowModal: hydrateWhenIdle(() => import('~/components/modals/WindowModal'))
  },
  layout: 'new',
  data() {
    return {
      modalRemoveTitle: this.$t('liquidity.remove_liquidity'),
      modalTitle: this.$t('pair.import_pair'),
      pairsList: [],
      pairsListLoading: true,
      pickedIndex: 0,
      pickedPool: null,
      transaction: {
        gasFee: 3000000,
        tx: '',
        loading: false,
        pending: false
      }
    }
  },
  async fetch({ store, error, route }) {
    try {
      const pageName = route.name.split('___')
      const seoData = store.getters['seos/seosByUri'](pageName[0])
      if (seoData === null) {
        await store.dispatch('seos/fetchSeos', {
          uri: route.name
        })
      }
    } catch (err) {
      error(err)
    }
  },
  head({ $config: { appUrl } }) {
    return {
      title: this.meta ? this.meta.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.meta
            ? this.meta.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.meta
            ? this.meta.image
            : appUrl + 'seo/default.jpg'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.meta ? this.meta.keyword : ''
        }
      ]
    }
  },
  computed: {
    ...mapGetters('auth', [
      'user'
    ]),
    ...mapGetters('pairs', [
      'pairs'
    ]),
    ...mapGetters('settings', [
      'deadline',
      'slippageTolerance',
      'speed'
    ]),
    ...mapGetters('seos', [
      'seosByUri'
    ]),
    meta() {
      const pageName = this.$nuxt.$route.name.split('___')
      return this.seosByUri(pageName[0])
    }
  },
  mounted() {
    if (window.ethereum.isConnected() && this.user !== undefined && this.user !== null) {
      const networkId = window.ethereum.networkVersion
      this.loadPairsList(networkId)
      this.$nuxt.$emit('tokenListToggle')
    } else {
      window.ethereum.on('connect', (connectInfo) => {
        if (this.user !== undefined && this.user !== null) {
          const networkId = window.eth.utils.hexToNumber(connectInfo.chainId)
          this.loadPairsList(networkId)
          this.$nuxt.$emit('tokenListToggle')
        }
      })
    }
  },
  methods: {
    ...mapActions('pairs', [
      'updatePairShare'
    ]),
    ...mapActions('modal', [
      'changeModalWalletState',
      'changeModalRemoveState',
      'changeModalGlobalState'
    ]),
    async openModalWallets() {
      await this.changeModalWalletState({
        state: true
      })
    },
    openImportModal() {
      this.changeModalGlobalState({
        state: true
      })
    },
    openRemoveModal() {
      this.changeModalRemoveState({
        state: true
      })
    },
    closeModal() {
      this.changeModalRemoveState({
        state: false
      })
    },
    addLiquidityRedirect(pool) {
      this.$router.push({
        path: this.localePath('/liquidity/add'),
        query: {
          from: pool.token0.address,
          to: pool.token1.address
        }
      })
    },
    reloadList() {
      const networkId = window.ethereum.networkVersion
      this.loadPairsList(networkId)
    },
    async prepareLiquidityRemovalApprove(pool, index, percent) {
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        return
      }
      const PeriodPairFile = await import('~/assets/abis/PeriodPair.json')
      const PeriodPair = PeriodPairFile.default
      const pairContract = new window.eth.eth.Contract(PeriodPair, pool.address)
      const token0 = await pairContract.methods.token0().call({ from: this.user.address })
      if (pool.token0.address.toLowerCase() !== token0.toLowerCase()) {
        await this.$removePair(pool.token0, pool.token1)
        const token1 = pool.token1
        pool.token1 = pool.token0
        pool.token0 = token1
        await this.$savePair(pool.token0, pool.token1, pool.address, false)
      }
      this.pickedPool = pool
      this.pickedIndex = index
      this.openRemoveModal()
    },
    async prepareLiquidityRemovalPermit(pool, index, percent) {
      this.transaction.loading = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        return
      }
      const PeriodPairFile = await import('~/assets/abis/PeriodPair.json')
      const PeriodPair = PeriodPairFile.default
      const pairContract = new window.eth.eth.Contract(PeriodPair, pool.address)
      const pairBalance = pool.balance !== null && pool.balance !== 0 ? pool.balance : 0
      const nonce = window.eth.utils.numberToHex(await pairContract.methods.nonces(this.user.address).call({ from: this.user.address }))
      const lpRemove = pairBalance * percent

      const EIP712Domain = [
        { name: 'name', type: 'string' },
        { name: 'version', type: 'string' },
        { name: 'chainId', type: 'uint256' },
        { name: 'verifyingContract', type: 'address' }
      ]
      const chainId = Number(await window.eth.eth.getChainId())
      const domain = {
        name: 'Defi Pool ' + pool.token0.symbol.toUpperCase() + '-' + pool.token1.symbol.toUpperCase() + ' LPs',
        version: '1',
        chainId,
        verifyingContract: pool.address
      }
      const Permit = [
        { name: 'owner', type: 'address' },
        { name: 'spender', type: 'address' },
        { name: 'value', type: 'uint256' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' }
      ]
      const message = {
        owner: this.user.address,
        spender: this.$config.periodRouter,
        value: lpRemove,
        nonce,
        deadline: this.$calculateDeadline(this.deadline)
      }
      const data = {
        types: {
          EIP712Domain,
          Permit
        },
        domain,
        primaryType: 'Permit',
        message
      }
      try {
        const signature = await this.$signTypedData(window.eth, this.user.address, data)
        this.removeLiquidity(signature, percent)
      } catch (err) {
        if (this.$refs.removeLiquidity !== undefined && this.$refs.removeLiquidity.rejectTransaction !== undefined) {
          this.$refs.removeLiquidity.rejectTransaction({
            type: 'error',
            message: this.$t('pools.signature_required')
          })
        }
        this.transaction.loading = true
      }
    },
    async removeLiquidity(signature = null, percent, receiveBnb = false) {
      const pool = this.pickedPool
      const index = this.pickedIndex

      let fromAddress = pool.token0.address
      let toAddress = pool.token1.address
      let bnbIsPresent = false
      if (fromAddress === this.$config.ZERO || fromAddress === this.$config.wbnbAddress) {
        fromAddress = this.$config.wbnbAddress
        bnbIsPresent = true
      }
      if (toAddress === this.$config.ZERO || toAddress === this.$config.wbnbAddress) {
        toAddress = this.$config.wbnbAddress
        bnbIsPresent = true
      }

      this.transaction.loading = true
      this.transaction.pending = true
      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        return
      }
      const PeriodRouterFile = await import('~/assets/abis/PeriodRouter.json')
      const PeriodRouter = PeriodRouterFile.default
      const routerContract = new window.eth.eth.Contract(PeriodRouter, this.$config.periodRouter)
      let tokenEntity = pool.token0
      let reserveTokenEntity = pool.reserves.reserve0
      let reserveETHEntity = pool.reserves.reserve1
      let reserveTokenDecimals = pool.token0.decimals
      let reserveETHDecimals = pool.token1.decimals
      if (this.$isStableCurrency(pool.token0.symbol)) {
        tokenEntity = pool.token1
        reserveTokenEntity = pool.reserves.reserve1
        reserveTokenDecimals = pool.token1.decimals
        reserveETHEntity = pool.reserves.reserve0
        reserveETHDecimals = pool.token0.decimals
      }
      const currentBalance = pool.balance !== null && pool.balance !== 0 ? pool.balance : 0
      const lpToRemove = percent * currentBalance
      const lpRatio = percent * currentBalance / pool.totalSupply
      const tokenAmount = lpRatio * reserveTokenEntity
      const ethAmount = lpRatio * reserveETHEntity

      const parameters = {
        token: this.$changeZeroToWrapped(tokenEntity.address),
        liquidity: this.$toWeiDecimal(Number(lpToRemove).toPrecision(10)),
        amountTokenMin: this.$toWeiDecimal(Number(tokenAmount - tokenAmount / 100 * this.slippageTolerance).toPrecision(10), reserveTokenDecimals),
        amountETHMin: this.$toWeiDecimal(Number(ethAmount - ethAmount / 100 * this.slippageTolerance).toPrecision(10), reserveETHDecimals),
        to: this.user.address,
        deadline: this.$calculateDeadline(this.deadline)
      }
      if (bnbIsPresent) {
        if (isNaN(Number(tokenAmount - tokenAmount / 100 * this.slippageTolerance)) ||
            isNaN(Number(ethAmount - ethAmount / 100 * this.slippageTolerance)) ||
            isNaN(lpToRemove)) {
          this.liquidityNotRemoved({
            code: 1,
            message: this.$t('liquidity.input_nan')
          })
          return
        }
        if (signature !== null) {
          parameters.approveMax = false
          parameters.v = signature.v
          parameters.r = signature.r
          parameters.s = signature.s
        }
        const gasPrice = await this.$calculateGasCost()
        try {
          if (signature !== null) {
            const gasFeeEstimated = await routerContract.methods
              .removeLiquidityETHWithPermit(
                parameters.token,
                parameters.liquidity,
                parameters.amountTokenMin,
                parameters.amountETHMin,
                parameters.to,
                parameters.deadline,
                parameters.approveMax,
                parameters.v,
                parameters.r,
                parameters.s
              ).estimateGas({ from: this.user.address, gas: this.transaction.gasFee })

            await routerContract.methods
              .removeLiquidityETHWithPermit(
                parameters.token,
                parameters.liquidity,
                parameters.amountTokenMin,
                parameters.amountETHMin,
                parameters.to,
                parameters.deadline,
                parameters.approveMax,
                parameters.v,
                parameters.r,
                parameters.s
              ).send({ from: this.user.address, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
              .then((result) => {
                this.liquidityRemoved(pool, percent, index, result)
              })
              .catch((err) => {
                this.liquidityNotRemoved(err)
              })
          } else if (receiveBnb) {
            const gasFeeEstimated = await routerContract.methods
              .removeLiquidityETH(
                parameters.token,
                parameters.liquidity,
                parameters.amountTokenMin,
                parameters.amountETHMin,
                parameters.to,
                parameters.deadline
              ).estimateGas({ from: this.user.address, gas: this.transaction.gasFee })

            await routerContract.methods
              .removeLiquidityETH(
                parameters.token,
                parameters.liquidity,
                parameters.amountTokenMin,
                parameters.amountETHMin,
                parameters.to,
                parameters.deadline
              ).send({ from: this.user.address, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
              .then((result) => {
                this.liquidityRemoved(pool, percent, index, result)
              })
              .catch((err) => {
                this.liquidityNotRemoved(err)
              })
          } else {
            const token0Amount = tokenAmount
            const token1Amount = ethAmount
            const tokenParams = {
              tokenA: this.$changeZeroToWrapped(pool.token0.address),
              tokenB: this.$changeZeroToWrapped(pool.token1.address),
              liquidity: parameters.liquidity,
              amountAMin: this.$toWeiDecimal(Number(token0Amount - token0Amount / 100 * this.slippageTolerance).toPrecision(10), pool.token0.decimals),
              amountBMin: this.$toWeiDecimal(Number(token1Amount - token1Amount / 100 * this.slippageTolerance).toPrecision(10), pool.token1.decimals),
              to: this.user.address,
              deadline: this.$calculateDeadline(this.deadline)
            }
            const gasFeeEstimated = await routerContract.methods
              .removeLiquidity(
                tokenParams.tokenA,
                tokenParams.tokenB,
                tokenParams.liquidity,
                tokenParams.amountAMin,
                tokenParams.amountBMin,
                tokenParams.to,
                tokenParams.deadline
              ).estimateGas({ from: this.user.address, gas: this.transaction.gasFee })

            await routerContract.methods
              .removeLiquidity(
                tokenParams.tokenA,
                tokenParams.tokenB,
                tokenParams.liquidity,
                tokenParams.amountAMin,
                tokenParams.amountBMin,
                tokenParams.to,
                tokenParams.deadline
              ).send({ from: this.user.address, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
              .then((result) => {
                this.liquidityRemoved(pool, percent, index, result)
              })
              .catch((err) => {
                this.liquidityNotRemoved(err)
              })
          }
        } catch (err) {
          this.liquidityNotRemoved(err)
        }
      } else {
        const reserveToken0 = pool.reserves.reserve0
        const reserveToken1 = pool.reserves.reserve1
        const currentBalance = pool.balance !== null && pool.balance !== 0 ? pool.balance : 0
        const lpRatio = percent * currentBalance / pool.totalSupply
        const token0Amount = lpRatio * reserveToken0
        const token1Amount = lpRatio * reserveToken1
        if (isNaN(Number(token0Amount - token0Amount / 100 * this.slippageTolerance)) ||
            isNaN(Number(token1Amount - token1Amount / 100 * this.slippageTolerance))) {
          this.liquidityNotRemoved({
            code: 1,
            message: this.$t('liquidity.input_nan')
          })
          return
        }
        const tokenParams = {
          tokenA: this.$changeZeroToWrapped(pool.token0.address),
          tokenB: this.$changeZeroToWrapped(pool.token1.address),
          liquidity: parameters.liquidity,
          amountAMin: this.$toWeiDecimal(Number(token0Amount - token0Amount / 100 * this.slippageTolerance).toPrecision(10), pool.token0.decimals),
          amountBMin: this.$toWeiDecimal(Number(token1Amount - token1Amount / 100 * this.slippageTolerance).toPrecision(10), pool.token1.decimals),
          to: this.user.address,
          deadline: this.$calculateDeadline(this.deadline)
        }
        console.log(tokenParams)
        if (signature !== null) {
          tokenParams.approveMax = false
          tokenParams.v = signature.v
          tokenParams.r = signature.r
          tokenParams.s = signature.s
        }
        const gasPrice = await this.$calculateGasCost()
        try {
          if (signature !== null) {
            const gasPrice = await this.$calculateGasCost()
            const gasFeeEstimated = await routerContract.methods
              .removeLiquidityWithPermit(
                tokenParams.tokenA,
                tokenParams.tokenB,
                tokenParams.liquidity,
                tokenParams.amountAMin,
                tokenParams.amountBMin,
                tokenParams.to,
                tokenParams.deadline,
                tokenParams.approveMax,
                tokenParams.v,
                tokenParams.r,
                tokenParams.s
              ).estimateGas({ from: this.user.address, gas: this.transaction.gasFee })

            await routerContract.methods
              .removeLiquidityWithPermit(
                tokenParams.tokenA,
                tokenParams.tokenB,
                tokenParams.liquidity,
                tokenParams.amountAMin,
                tokenParams.amountBMin,
                tokenParams.to,
                tokenParams.deadline,
                tokenParams.approveMax,
                tokenParams.v,
                tokenParams.r,
                tokenParams.s
              ).send({ from: this.user.address, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
              .then((result) => {
                this.liquidityRemoved(pool, percent, index, result)
              })
              .catch((err) => {
                this.liquidityNotRemoved(err)
              })
          } else {
            const gasFeeEstimated = await routerContract.methods
              .removeLiquidity(
                tokenParams.tokenA,
                tokenParams.tokenB,
                tokenParams.liquidity,
                tokenParams.amountAMin,
                tokenParams.amountBMin,
                tokenParams.to,
                tokenParams.deadline
              ).estimateGas({ from: this.user.address, gas: this.transaction.gasFee })

            await routerContract.methods
              .removeLiquidity(
                tokenParams.tokenA,
                tokenParams.tokenB,
                tokenParams.liquidity,
                tokenParams.amountAMin,
                tokenParams.amountBMin,
                tokenParams.to,
                tokenParams.deadline
              ).send({ from: this.user.address, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
              .then((result) => {
                this.liquidityRemoved(pool, percent, index, result)
              })
              .catch((err) => {
                this.liquidityNotRemoved(err)
              })
          }
        } catch (err) {
          this.liquidityNotRemoved(err)
        }
      }
    },
    async liquidityRemoved(pool, percent, index, result) {
      this.transaction.loading = false
      this.transaction.pending = false
      this.transaction.tx = result.transactionHash

      const currentBalance = pool.balance !== null && pool.balance !== 0 ? pool.balance : 0
      const lpRatio = percent * currentBalance / pool.totalSupply
      const token0Amount = lpRatio * pool.reserves.reserve0
      const token1Amount = lpRatio * pool.reserves.reserve1
      this.$saveTransaction({
        tid0: pool.token0.address,
        tid1: pool.token1.address,
        tid0_token: pool.token0,
        tid1_token: pool.token1,
        t0_amount: token0Amount,
        t1_amount: token1Amount,
        hash: result.transactionHash,
        gasUsed: result.gasUsed,
        transactionIndex: result.transactionIndex,
        type: 'remove'
      },
      this.$t('common.remove_action') + ' ' + this.$formatCryptoAmount(token0Amount, 6) + ' ' + pool.token0.symbol +
        ' ' + this.$t('common.and') + ' ' + this.$formatCryptoAmount(token1Amount, 6) + ' ' + pool.token1.symbol
      )
      this.updatePoolShare(pool, {
        tid0: pool.token0.address,
        tid1: pool.token1.address,
        t0_amount: token0Amount,
        t1_amount: token1Amount
      })

      this.changeModalRemoveState({
        state: true
      })
      if (this.$refs.removeLiquidity !== undefined && this.$refs.removeLiquidity.initSuccessScreen !== undefined) {
        this.$refs.removeLiquidity.initSuccessScreen()
      }

      await this.$removePair(pool.token0, pool.token1)
      await this.$savePair(pool.token0, pool.token1, pool.address, false)
      this.reloadList()
    },
    removePairFromLocalList(poolToDelete) {
      const resultList = this.pairsList.filter(pool =>
        pool.token0.address.toLowerCase() === poolToDelete.token0.address.toLowerCase() &&
        pool.token1.address.toLowerCase() === poolToDelete.token1.address.toLowerCase()
      )
      this.pairsList = resultList
    },
    liquidityNotRemoved(err) {
      this.transaction.loading = false
      this.transaction.pending = false
      if (err.code === 4001) {
        this.changeModalRemoveState({
          state: true
        })
        if (this.$refs.removeLiquidity !== undefined && this.$refs.removeLiquidity.rejectTransaction !== undefined) {
          this.$refs.removeLiquidity.rejectTransaction({
            type: 'denied'
          })
        }
      } else {
        const errorMessage = err.message !== undefined && err.message.length > 0 ? this.$getRPCError(err.message) : ''
        this.changeModalRemoveState({
          state: true
        })
        if (this.$refs.removeLiquidity !== undefined && this.$refs.removeLiquidity.rejectTransaction !== undefined) {
          this.$refs.removeLiquidity.rejectTransaction({
            type: 'error',
            message: errorMessage
          })
        }
      }
    },
    async loadPairsList(networkId) {
      this.pairsListLoading = true
      let userPairs = this.$cookies.get(networkId + '/' + this.user.address)
      if (userPairs === undefined && networkId !== null && this.user.address !== null) {
        await this.$createUserActivity()
        userPairs = this.$cookies.get(networkId + '/' + this.user.address)
      }
      if (networkId !== null && userPairs !== undefined && this.user.address !== null) {
        const PeriodFactoryFile = await import('~/assets/abis/PeriodFactory.json')
        const PeriodFactory = PeriodFactoryFile.default
        const factoryContract = new window.eth.eth.Contract(PeriodFactory, this.$config.periodFactory)
        this.pairsList = []
        for (const pair in userPairs.pairs) {
          if (userPairs.pairs[pair].reserves === undefined || Object.keys(userPairs.pairs[pair].reserves).length === 0) {
            const pairKey = this.$changeZeroToWrapped(userPairs.pairs[pair].token0.address) + '/' + this.$changeZeroToWrapped(userPairs.pairs[pair].token1.address)
            const pairAddress = await factoryContract.methods
              .getPair(this.$changeZeroToWrapped(userPairs.pairs[pair].token0.address), this.$changeZeroToWrapped(userPairs.pairs[pair].token1.address))
              .call({ from: this.user.address })
            if (pairAddress !== this.$config.ZERO) {
              const pool = {
                token0: userPairs.pairs[pair].token0,
                token1: userPairs.pairs[pair].token1,
                balance: 0,
                totalSupply: 0,
                poolShare: 0,
                reserves: {},
                address: pairAddress
              }
              const PeriodPairFile = await import('~/assets/abis/PeriodPair.json')
              const PeriodPair = PeriodPairFile.default
              const pairContract = new window.eth.eth.Contract(PeriodPair, pairAddress)
              pool.reserves = await pairContract.methods.getReserves().call({ from: this.user.address })
              pool.balance = await pairContract.methods.balanceOf(this.user.address).call({ from: this.user.address })
              pool.totalSupply = await pairContract.methods.totalSupply().call({ from: this.user.address })
              pool.balance = parseFloat(window.eth.utils.fromWei(pool.balance))

              const reserve0Decimal = this.$getDecimalsName(parseInt(userPairs.pairs[pair].token0.decimals))
              let reserve0Balance = parseFloat(window.eth.utils.fromWei(pool.reserves.reserve0, reserve0Decimal.name))
              if (reserve0Decimal.multiply > 0) {
                reserve0Balance /= 10 ** reserve0Decimal.multiply
              }
              const reserve1Decimal = this.$getDecimalsName(parseInt(userPairs.pairs[pair].token1.decimals))
              let reserve1Balance = parseFloat(window.eth.utils.fromWei(pool.reserves.reserve1, reserve1Decimal.name))
              if (reserve1Decimal.multiply > 0) {
                reserve1Balance /= 10 ** reserve1Decimal.multiply
              }
              pool.reserves.reserve0 = reserve0Balance
              pool.reserves.reserve1 = reserve1Balance
              pool.totalSupply = parseFloat(window.eth.utils.fromWei(pool.totalSupply))
              pool.poolShare = pool.balance / pool.totalSupply
              this.pairsList.push(pool)
              if (userPairs.pairs !== undefined) {
                if (userPairs.pairs[pairKey] === undefined) {
                  userPairs.pairs[networkId][pairKey] = pool
                  this.$cookies.set(networkId + '/' + this.user.address, userPairs)
                }
              } else {
                userPairs.pairs = {}
                userPairs.pairs[pairKey] = pool
                this.$cookies.set(networkId + '/' + this.user.address, userPairs)
              }
            }
          } else {
            this.pairsList.push(userPairs.pairs[pair])
          }
        }
        this.pairsListLoading = false
      } else {
        this.pairsListLoading = false
      }
    },
    changeModalTitle(titleType) {
      switch (titleType) {
        case 'init':
          this.modalRemoveTitle = this.$t('liquidity.you_will_receive')
          break
        case 'loading':
          this.modalRemoveTitle = this.$t('swap.waiting_for_confirmation')
          break
        case 'success':
          this.modalRemoveTitle = this.$t('swap.success')
          break
        case 'error':
          this.modalRemoveTitle = this.$t('swap.error_header')
          break
        default:
          this.modalRemoveTitle = this.$t('liquidity.remove_liquidity')
      }
    },
    async updatePoolShare(pool, summary) {
      const PeriodPairFile = await import('~/assets/abis/PeriodPair.json')
      const PeriodPair = PeriodPairFile.default
      const pairContract = new window.eth.eth.Contract(PeriodPair, pool.address)
      const balance = await pairContract.methods.balanceOf(this.user.address).call({ from: this.user.address })
      const totalSupply = await pairContract.methods.totalSupply().call({ from: this.user.address })
      const reservres = await pairContract.methods.getReserves().call({ from: this.user.address })
      this.updatePairShare({
        ...summary,
        chainId: this.$config.chainId,
        address: pool.address,
        share: balance / totalSupply * 100,
        totalSupply: parseFloat(window.eth.utils.fromWei(totalSupply)),
        reservres
      })
    }
  }
}
</script>

<style scoped>
  @import url('~/assets/css/new/liquidity.css');
</style>
